import React from 'react';
import { Hash, Store, CreditCard, FileText, Mail, Briefcase, Calendar, Globe } from 'lucide-react';
import { fiscalVariantForCountry } from '../../../lib/utils/countries';
import { PropTextRow } from './rows';
import { PropRow } from '../../shared/activity-drawer/ui/PropRow';
import { WebsiteEnrichField, type WebsiteEnrichResult } from '../WebsiteEnrichField';

interface FiscalSectionProps {
  formData: Record<string, any>;
  updateFormData: (updates: Record<string, any>) => void;
  validationErrors: Record<string, string>;
}

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

/**
 * Sezione "Dati aziendali e fiscali" a righe di proprietà, adattiva per paese:
 * SDI/PEC/ATECO/CF solo Italia (o se già valorizzati). La P.IVA vive sotto il
 * titolo del drawer (flusso "P.IVA-first"), non qui.
 */
export function FiscalSection({ formData, updateFormData, validationErrors }: FiscalSectionProps) {
  const fiscalVariant = fiscalVariantForCountry(formData.country);
  const isIt = fiscalVariant === 'it';
  const isCompany = formData.entity_type === 'company';

  // Arricchimento dal sito: compila SOLO i campi ancora vuoti
  const handleWebsiteEnrich = (data: WebsiteEnrichResult) => {
    const updates: Record<string, any> = {};
    if (data.email && !formData.email) updates.email = data.email;
    if (data.phone && !formData.phone) updates.phone = data.phone;
    if (data.pec && !formData.pec) updates.pec = data.pec;
    if (data.vat_number && !formData.vat_number) updates.vat_number = data.vat_number;
    if (Object.keys(updates).length > 0) updateFormData(updates);
  };

  return (
    <div>
      <PropTextRow
        icon={rowIcon(Hash)}
        label="Codice cliente"
        value={formData.client_code || ''}
        onChange={(value) => updateFormData({ client_code: value })}
      />
      {isCompany && (
        <PropTextRow
          icon={rowIcon(Store)}
          label="Nome insegna"
          value={formData.trade_name || ''}
          onChange={(value) => updateFormData({ trade_name: value })}
        />
      )}
      {(isIt || formData.tax_code) && (
        <PropTextRow
          icon={rowIcon(CreditCard)}
          label="Codice Fiscale"
          value={formData.tax_code || ''}
          onChange={(value) => updateFormData({ tax_code: value })}
          error={validationErrors.tax_code}
        />
      )}
      {!isCompany && (
        <PropTextRow
          icon={rowIcon(Calendar)}
          label="Data di nascita"
          type="date"
          value={formData.birth_date || ''}
          onChange={(value) => updateFormData({ birth_date: value })}
          placeholder=""
        />
      )}
      {isCompany && (isIt || formData.sdi_code) && (
        <PropTextRow
          icon={rowIcon(FileText)}
          label="Codice SDI"
          value={formData.sdi_code || ''}
          onChange={(value) => updateFormData({ sdi_code: value })}
        />
      )}
      {(isIt || formData.pec) && (
        <PropTextRow
          icon={rowIcon(Mail)}
          label="PEC"
          type="email"
          value={formData.pec || ''}
          onChange={(value) => updateFormData({ pec: value })}
        />
      )}
      <PropTextRow
        icon={rowIcon(Mail)}
        label="Mail amministr."
        type="email"
        value={formData.email_amministrazione || ''}
        onChange={(value) => updateFormData({ email_amministrazione: value })}
      />
      {isCompany && (isIt || formData.ateco_code) && (
        <PropTextRow
          icon={rowIcon(Briefcase)}
          label="Codice ATECO"
          value={formData.ateco_code || ''}
          onChange={(value) => updateFormData({ ateco_code: value })}
        />
      )}
      {isCompany && (
        <div>
          <PropRow icon={rowIcon(Globe)} label="Sito web">
            <div className="min-w-0 flex-1">
              <WebsiteEnrichField
                bare
                value={formData.website || ''}
                onChange={(value) => updateFormData({ website: value })}
                onEnrich={handleWebsiteEnrich}
              />
            </div>
          </PropRow>
          {validationErrors.website && (
            <p className="ml-[8.5rem] text-[11px] text-red-500">{validationErrors.website}</p>
          )}
        </div>
      )}
      {!isIt && (
        <div className="mt-2 flex gap-2 rounded-md bg-[#F7F7F5] dark:bg-white/[0.05] px-3 py-2 text-[12px] text-[#787774] dark:text-gray-300">
          <span>
            Cliente estero ({formData.country}): in fattura elettronica il codice destinatario sarà
            XXXXXXX. PEC, SDI e ATECO non sono richiesti.
          </span>
        </div>
      )}
    </div>
  );
}
